import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle, ChevronDown } from "lucide-react";
import { useConsultation } from "../context/ConsultationContext";
import { whatsappLink } from "../data";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "./ui/dropdown-menu";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const LEGAL = [
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms of Service" },
  { to: "/disclaimer", label: "Disclaimer" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openModal } = useConsultation();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
        scrolled ? "border-b border-white/10 bg-[#050505]/70 backdrop-blur-2xl" : "border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
        <Link to="/" className="font-display text-2xl font-semibold tracking-tight" data-testid="navbar-logo">
          JOBL<span className="text-blue-500">SX</span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm transition-colors duration-200 hover:text-white ${isActive ? "text-white" : "text-slate-400"}`
              }
            >
              {l.label}
            </NavLink>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger
              className="flex items-center gap-1 text-sm text-slate-400 outline-none transition-colors duration-200 hover:text-white data-[state=open]:text-white"
              data-testid="nav-legal-trigger"
            >
              Legal <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="border-white/10 bg-[#0a0a0a]/95 backdrop-blur-2xl"
            >
              {LEGAL.map((l) => (
                <DropdownMenuItem key={l.to} asChild className="cursor-pointer text-slate-300 focus:bg-white/10 focus:text-white">
                  <Link to={l.to} data-testid={`nav-legal-${l.to.slice(1)}`}>{l.label}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-white/10"
            data-testid="navbar-whatsapp-button"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          <button
            onClick={openModal}
            className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-[0_0_20px_rgba(37,99,235,0.4)] transition-all duration-300 hover:bg-blue-700 hover:shadow-[0_0_28px_rgba(37,99,235,0.6)]"
            data-testid="navbar-book-button"
          >
            Book Consultation
          </button>
        </div>

        <button
          className="text-white md:hidden"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-white/10 bg-[#050505]/95 backdrop-blur-2xl md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-3 text-base transition-colors ${isActive ? "bg-white/5 text-white" : "text-slate-300"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="my-2 border-t border-white/10" />
              {LEGAL.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.to.slice(1)}`}
                  className="rounded-lg px-3 py-2.5 text-sm text-slate-400 transition-colors hover:text-white"
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                onClick={openModal}
                className="mt-2 rounded-full bg-blue-600 px-5 py-3 text-center font-medium text-white"
                data-testid="mobile-book-button"
              >
                Book Consultation
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
